import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { AsociadoCompleto } from '@/lib/typesNeoCore'

/**
 * Edición de Contactabilidad por sección.
 *
 * El RPC `actualizar_contactabilidad(p_asociado_id, p_campos jsonb)` ya existe
 * desde la migración 0014 — hace upsert si la row no existía, así que sirve
 * tanto para crear contactabilidad de cero como para editar.
 */

export type ContactoSeccion = 'email' | 'telefonos' | 'optins' | 'emergencia' | 'preferencias'

const TITULOS: Record<ContactoSeccion, string> = {
  email: 'Email',
  telefonos: 'Teléfonos',
  optins: 'Opt-ins de comunicación',
  emergencia: 'Contacto de emergencia',
  preferencias: 'Preferencias',
}

const RELACIONES_EMERGENCIA = [
  'CONYUGE', 'PADRE', 'MADRE', 'HIJO', 'HIJA', 'HERMANO', 'HERMANA',
  'ABUELO', 'ABUELA', 'TIO', 'TIA', 'PRIMO', 'PRIMA', 'AMIGO', 'OTRO',
] as const

type Contacto = NonNullable<AsociadoCompleto['contactabilidad']>

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  seccion: ContactoSeccion | null
  contacto: Contacto | null
  onSuccess: () => void
}

export function EditarContactoSeccionModal({ open, onClose, asociadoId, seccion, contacto, onSuccess }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initial = useMemo<Record<string, unknown>>(() => {
    if (!seccion) return {}
    return camposSeccion(seccion, contacto)
  }, [contacto, seccion])

  useEffect(() => {
    if (!open) return
    setValues(initial)
    setError(null)
  }, [open, initial])

  const setField = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const diff: Record<string, unknown> = {}
    for (const k of Object.keys(values)) {
      if (norm(values[k]) !== norm(initial[k])) {
        diff[k] = values[k] === '' ? null : values[k]
      }
    }
    if (Object.keys(diff).length === 0) {
      setError('No hubo cambios para guardar.')
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('actualizar_contactabilidad', {
      p_asociado_id: asociadoId,
      p_campos: diff,
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo guardar: ${rpcErr.message}`)
      return
    }

    onSuccess()
    onClose()
  }

  if (!seccion) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Editar · ${TITULOS[seccion]}`}
      subtitle="Los cambios se aplican al perfil del asociado y respetan los opt-ins."
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="editar-contacto-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id="editar-contacto-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        {seccion === 'email' && (
          <>
            <Field label="Email principal">
              <input type="email" value={(values.email_principal as string) ?? ''} onChange={(e) => setField('email_principal', e.target.value.toLowerCase())} />
            </Field>
            <Field label="Email secundario (opcional)">
              <input type="email" value={(values.email_secundario as string) ?? ''} onChange={(e) => setField('email_secundario', e.target.value.toLowerCase())} />
            </Field>
          </>
        )}

        {seccion === 'telefonos' && (
          <>
            <Field label="Móvil (formato E.164, ej. +50258123456)">
              <input value={(values.telefono_movil_e164 as string) ?? ''} onChange={(e) => setField('telefono_movil_e164', e.target.value.replace(/\s/g, ''))} placeholder="+50258123456" />
            </Field>
            <Grid cols={2}>
              <Field label="Teléfono casa">
                <input value={(values.telefono_casa as string) ?? ''} onChange={(e) => setField('telefono_casa', e.target.value)} />
              </Field>
              <Field label="Teléfono trabajo">
                <input value={(values.telefono_trabajo as string) ?? ''} onChange={(e) => setField('telefono_trabajo', e.target.value)} />
              </Field>
            </Grid>
          </>
        )}

        {seccion === 'optins' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 12, color: 'var(--n-500)', margin: 0, lineHeight: 1.5 }}>
              Por qué canal podemos comunicarnos con el asociado. Los opt-out
              respetan políticas de privacidad y reducen riesgo de bloqueo.
            </p>
            <CheckRow checked={!!values.whatsapp_optin} onChange={(v) => setField('whatsapp_optin', v)} label="WhatsApp" hint="Avisos transaccionales y soporte." />
            <CheckRow checked={!!values.sms_optin} onChange={(v) => setField('sms_optin', v)} label="SMS" hint="Códigos de verificación y alertas urgentes." />
            <CheckRow checked={!!values.email_optin} onChange={(v) => setField('email_optin', v)} label="Email" hint="Estados de cuenta, comprobantes, novedades." />
            <CheckRow checked={!!values.push_optin} onChange={(v) => setField('push_optin', v)} label="Push (app móvil)" hint="Notificaciones en tiempo real desde la app." />
          </div>
        )}

        {seccion === 'emergencia' && (
          <>
            <Grid cols={2}>
              <Field label="Nombre completo">
                <input value={(values.emergencia_nombre as string) ?? ''} onChange={(e) => setField('emergencia_nombre', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Teléfono">
                <input value={(values.emergencia_telefono as string) ?? ''} onChange={(e) => setField('emergencia_telefono', e.target.value)} />
              </Field>
            </Grid>
            <Field label="Relación con el asociado">
              <select value={(values.emergencia_relacion as string) ?? ''} onChange={(e) => setField('emergencia_relacion', e.target.value)}>
                <option value="">— Sin especificar —</option>
                {RELACIONES_EMERGENCIA.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </>
        )}

        {seccion === 'preferencias' && (
          <Grid cols={2}>
            <Field label="Idioma preferente">
              <select value={(values.idioma_preferencia as string) ?? ''} onChange={(e) => setField('idioma_preferencia', e.target.value)}>
                <option value="es-GT">Español (Guatemala)</option>
                <option value="en-US">English (US)</option>
              </select>
            </Field>
            <Field label="Zona horaria">
              <select value={(values.zona_horaria as string) ?? ''} onChange={(e) => setField('zona_horaria', e.target.value)}>
                <option value="America/Guatemala">America/Guatemala</option>
              </select>
            </Field>
          </Grid>
        )}
      </form>
    </Modal>
  )
}

// ---------- Helpers ----------

function camposSeccion(seccion: ContactoSeccion, c: Contacto | null): Record<string, unknown> {
  switch (seccion) {
    case 'email':
      return {
        email_principal: c?.email_principal ?? '',
        email_secundario: c?.email_secundario ?? '',
      }
    case 'telefonos':
      return {
        telefono_movil_e164: c?.telefono_movil_e164 ?? c?.telefono_movil ?? '',
        telefono_casa: c?.telefono_casa ?? '',
        telefono_trabajo: c?.telefono_trabajo ?? '',
      }
    case 'optins':
      return {
        whatsapp_optin: c?.whatsapp_optin ?? true,
        sms_optin: c?.sms_optin ?? true,
        email_optin: c?.email_optin ?? true,
        push_optin: c?.push_optin ?? true,
      }
    case 'emergencia':
      return {
        emergencia_nombre: c?.emergencia_nombre ?? '',
        emergencia_telefono: c?.emergencia_telefono ?? '',
        emergencia_relacion: c?.emergencia_relacion ?? '',
      }
    case 'preferencias':
      return {
        idioma_preferencia: c?.idioma_preferencia ?? 'es-GT',
        zona_horaria: c?.zona_horaria ?? 'America/Guatemala',
      }
  }
}

function norm(v: unknown): unknown {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v
  return String(v).trim()
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="form-label" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

function Grid({ cols, children }: { cols: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
      {children}
    </div>
  )
}

function CheckRow({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label
      style={{
        display: 'flex', gap: 12, padding: '10px 12px',
        background: checked ? 'var(--brand-50)' : 'var(--surface)',
        border: `1px solid ${checked ? 'var(--brand-300, var(--brand-600))' : 'var(--border)'}`,
        borderRadius: 8, cursor: 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>{hint}</span>
      </span>
    </label>
  )
}
